/**
 * Training Calendar — read-only aggregated view of dated training events.
 *
 * Streams from GET /api/v1/calendar/events?start=&end=
 *   batch_start · batch_end · live_session · assignment_due
 *
 * Month / Week / Agenda views; per-type filters; prev/next/today nav.
 * Read-only (viewer-readable); clicking an event deep-links to its surface.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  addMonths, addWeeks, format, isSameMonth, isSameDay, parseISO, isToday,
} from 'date-fns';
import { useApi } from '../../hooks/useApi';
import { SkeletonCard } from '../../components/common/SkeletonCard';
import { EmptyState } from '../../components/common/EmptyState';
import { Button } from '../../components/ui/button';
import {
  CalendarDays, ChevronLeft, ChevronRight, Play, Flame, GraduationCap,
  ClipboardList, CalendarOff,
} from 'lucide-react';

const TYPE_META = {
  batch_start:    { label: 'Batch start',  icon: GraduationCap, dot: '#10b981', chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
  batch_end:      { label: 'Batch end',    icon: GraduationCap, dot: '#f43f5e', chip: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30' },
  live_session:   { label: 'Live session', icon: Flame,         dot: '#f59e0b', chip: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
  assignment_due: { label: 'Assignment',   icon: ClipboardList, dot: '#6366f1', chip: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 border-indigo-500/30' },
};
const ALL_TYPES = Object.keys(TYPE_META);
const ymd = (d) => format(d, 'yyyy-MM-dd');

export default function TrainingCalendarPage() {
  const { get } = useApi();
  const navigate = useNavigate();
  const [view, setView] = useState('month');        // month | week | agenda
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState(null);
  const [active, setActive] = useState(new Set(ALL_TYPES));

  // Visible date range drives both the grid and the fetch window.
  const range = useMemo(() => {
    if (view === 'week') {
      return { from: startOfWeek(cursor, { weekStartsOn: 0 }), to: endOfWeek(cursor, { weekStartsOn: 0 }) };
    }
    if (view === 'agenda') {
      return { from: startOfMonth(cursor), to: endOfMonth(addMonths(cursor, 2)) };
    }
    return {
      from: startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 }),
      to: endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 }),
    };
  }, [view, cursor]);

  const load = useCallback(() => {
    setEvents(null);
    get(`/calendar/events?start=${ymd(range.from)}&end=${ymd(range.to)}`)
      .then((r) => setEvents(r.events || []))
      .catch(() => setEvents([]));
  }, [get, range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const m = {};
    (events || []).filter((e) => active.has(e.type)).forEach((e) => {
      (m[e.date] = m[e.date] || []).push(e);
    });
    return m;
  }, [events, active]);

  const toggleType = (t) => setActive((prev) => {
    const next = new Set(prev);
    next.has(t) ? next.delete(t) : next.add(t);
    return next;
  });

  const step = (dir) => setCursor((c) => (view === 'week' ? addWeeks(c, dir) : addMonths(c, dir)));

  const days = useMemo(
    () => eachDayOfInterval({ start: range.from, end: range.to }),
    [range.from, range.to]
  );

  const headerLabel = view === 'week'
    ? `${format(range.from, 'MMM d')} – ${format(range.to, 'MMM d, yyyy')}`
    : format(cursor, 'MMMM yyyy');

  return (
    <div className="space-y-5 relative" data-testid="training-calendar">
      <div aria-hidden className="page-blob bg-[var(--lumyn-content)]" />
      <div className="relative z-10 space-y-5">
        {/* Header */}
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)] text-[11px] font-semibold uppercase tracking-[0.14em]">
            <CalendarDays className="w-3.5 h-3.5" />Calendar
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Training Calendar</h1>
          <p className="text-sm text-muted-foreground">
            Batch milestones, live sessions and assignment deadlines — one read-only view.
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => step(-1)} data-testid="cal-prev" aria-label="Previous">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
            <Button variant="outline" size="sm" onClick={() => step(1)} data-testid="cal-next" aria-label="Next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <span className="ml-2 font-display text-lg font-semibold tabular-nums" data-testid="cal-label">{headerLabel}</span>
          </div>
          <div className="inline-flex rounded-lg border border-border overflow-hidden">
            {['month', 'week', 'agenda'].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`cal-view-${v}`}
                className={`px-3 py-1.5 text-sm capitalize transition-colors ${
                  view === v ? 'bg-[var(--lumyn-content)] text-white' : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Type filters */}
        <div className="flex flex-wrap gap-2" data-testid="cal-filters">
          {ALL_TYPES.map((t) => {
            const meta = TYPE_META[t];
            const on = active.has(t);
            const Icon = meta.icon;
            return (
              <button
                key={t}
                onClick={() => toggleType(t)}
                data-testid={`cal-filter-${t}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition ${
                  on ? meta.chip : 'bg-muted/40 text-muted-foreground border-border opacity-60'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />{meta.label}
              </button>
            );
          })}
        </div>

        {events == null ? (
          <SkeletonCard />
        ) : view === 'agenda' ? (
          <AgendaView byDay={byDay} navigate={navigate} />
        ) : (
          <MonthGrid days={days} byDay={byDay} cursor={cursor} view={view} navigate={navigate} />
        )}
      </div>
    </div>
  );
}

function EventChip({ ev, navigate }) {
  const meta = TYPE_META[ev.type];
  const Icon = meta.icon;
  return (
    <button
      onClick={() => ev.link && navigate(ev.link)}
      data-testid={`cal-event-${ev.id}`}
      title={ev.title}
      className={`w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] border ${meta.chip} hover:brightness-95 transition`}
    >
      <Icon className="inline w-3 h-3 mr-1 -mt-0.5" />{ev.title}
    </button>
  );
}

function MonthGrid({ days, byDay, cursor, view, navigate }) {
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return (
    <div className="rounded-xl border border-border overflow-hidden bg-card" data-testid="cal-grid">
      <div className="grid grid-cols-7 border-b border-border bg-muted/30">
        {weekdays.map((d) => (
          <div key={d} className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground text-center">{d}</div>
        ))}
      </div>
      <div className={`grid grid-cols-7 ${view === 'week' ? '' : 'auto-rows-fr'}`}>
        {days.map((day) => {
          const key = ymd(day);
          const list = byDay[key] || [];
          const muted = view === 'month' && !isSameMonth(day, cursor);
          return (
            <div
              key={key}
              className={`min-h-[104px] border-b border-r border-border p-1.5 space-y-1 ${muted ? 'bg-muted/20' : ''}`}
            >
              <div className="flex justify-end">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs tabular-nums ${
                  isToday(day) ? 'bg-[var(--lumyn-content)] text-white font-semibold' : muted ? 'text-muted-foreground/50' : 'text-foreground'
                }`}>
                  {format(day, 'd')}
                </span>
              </div>
              {list.slice(0, 4).map((ev) => <EventChip key={ev.id} ev={ev} navigate={navigate} />)}
              {list.length > 4 && (
                <div className="text-[10px] text-muted-foreground pl-1">+{list.length - 4} more</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgendaView({ byDay, navigate }) {
  const dates = Object.keys(byDay).sort();
  if (dates.length === 0) {
    return <EmptyState icon={CalendarOff} title="Nothing scheduled" description="No batch milestones, live sessions or assignment deadlines in this window." />;
  }
  return (
    <div className="space-y-3" data-testid="cal-agenda">
      {dates.map((d) => (
        <div key={d} className="rounded-xl border border-border bg-card p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-sm font-display font-semibold ${isSameDay(parseISO(d), new Date()) ? 'text-[var(--lumyn-content)]' : ''}`}>
              {format(parseISO(d), 'EEEE, MMM d')}
            </span>
            {isSameDay(parseISO(d), new Date()) && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[var(--lumyn-content)]/15 text-[var(--lumyn-content)]">Today</span>}
          </div>
          <div className="space-y-1.5">
            {byDay[d].map((ev) => {
              const meta = TYPE_META[ev.type];
              const Icon = meta.icon;
              return (
                <button
                  key={ev.id}
                  onClick={() => ev.link && navigate(ev.link)}
                  data-testid={`cal-event-${ev.id}`}
                  className="w-full flex items-center gap-3 rounded-lg border border-border p-2 hover:border-[var(--lumyn-content)] transition text-left"
                >
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: meta.dot }} />
                  <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-sm">{ev.title}</span>
                  <span className="text-[11px] text-muted-foreground">{meta.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
